/// <amd-module name="Reporting/BurndownReport/BurndownReportUserSettings" />
import { AggregationSettings } from "Reporting/BoardsCommon/Pickers/AggregationPicker";
import { WorkItemBacklogFilter } from "Reporting/BoardsCommon/Pickers/WorkItemBacklogFilter";
import { IVssPageContext } from "VSS/Platform/Context";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { AnalyticsSprintBurndownSettings } from "Widgets/AnalyticsSprintBurndown/AnalyticsSprintBurndownWidgetComponents/AnalyticsSprintBurndownSettings";

/* Contract for the user preferences stored in the platform settings service*/
export interface BurndownReportUserSettingsData {
    /** Describes the last visited iteration. Start and end date settings are only preserved if the user is looking at the last saved iteration. */
    iterationId?: string;
    startDate?: string;
    endDate?: string;

    /** Describes the selected iteration for all Burndowns. */
    aggregation?: AggregationSettings;
    workItemTypeFilter?: WorkItemBacklogFilter;

    showNonWorkingDays?: boolean;

    showRemainingCapacityAtStartOfDay?: boolean;

    /* Consider work items with State or StateCategory 'Resolved' as Completed work items */
    showResolvedItemsAsCompleted?: boolean;
}

/* Encapsulate state and behavior for the user preferences stored in and retrieved from the platform settings service*/
export class BurndownReportUserSettings {
    public static aggregationKey = "AggregationSettings";
    public static startDateKey = "StartDate";
    public static endDateKey = "EndDate";
    public static iterationIdKey = "IterationId";
    public static workItemTypeFilterKey = "WorkItemTypeFilter";
    public static showNonWorkingDaysKey = "ShowNonWorkingDays";
    public static showRemainingCapacityAtStartOfDayKey = "ShowRemainingCapacityAtStartOfDay"
    public static showResolvedItemsAsCompletedKey = "ShowResolvedItemsAsCompleted";
    public static teamScopeName = "team";

    public static async reset(pageContext: IVssPageContext, teamId: string): Promise<void> {
        const settingsService = pageContext.getService<ISettingsService>("ISettingsService");
        const reportKey = getBurndownReportSettingsStorageKey();
        await settingsService.removeEntries(reportKey, SettingsUserScope.Me, BurndownReportUserSettings.teamScopeName, teamId);
    }

    public static async save(pageContext: IVssPageContext, newSettings: AnalyticsSprintBurndownSettings, teamId: string): Promise<void> {
        const settingsService = pageContext.getService<ISettingsService>("ISettingsService");
        const reportKey = getBurndownReportSettingsStorageKey();
        const userSettings: {
            [key: string]: any;
        } = {};
        userSettings[reportKey + "/" + BurndownReportUserSettings.iterationIdKey] = newSettings.iterationId;
        userSettings[reportKey + "/" + BurndownReportUserSettings.startDateKey] = newSettings.timePeriodConfiguration.startDate;
        userSettings[reportKey + "/" + BurndownReportUserSettings.endDateKey] = newSettings.timePeriodConfiguration.endDate;
        userSettings[reportKey + "/" + BurndownReportUserSettings.aggregationKey] = newSettings.aggregation;
        userSettings[reportKey + "/" + BurndownReportUserSettings.workItemTypeFilterKey] = newSettings.workItemTypeFilter;
        userSettings[reportKey + "/" + BurndownReportUserSettings.showNonWorkingDaysKey] = newSettings.showNonWorkingDays;
        userSettings[reportKey + "/" + BurndownReportUserSettings.showRemainingCapacityAtStartOfDayKey] = newSettings.showRemainingCapacityAtStartOfDay;
        userSettings[reportKey + "/" + BurndownReportUserSettings.showResolvedItemsAsCompletedKey] = newSettings.showResolvedItemsAsCompletedEnabled;
        
        await settingsService.setEntries(userSettings, SettingsUserScope.Me, BurndownReportUserSettings.teamScopeName, teamId);
    }

    public static async load(pageContext: IVssPageContext, teamId: string): Promise<BurndownReportUserSettingsData> {
        const settingsService = pageContext.getService<ISettingsService>("ISettingsService");

        const userSettings: {
            [key: string]: any;
        } = await settingsService.getEntriesAsync(
            getBurndownReportSettingsStorageKey(),
            SettingsUserScope.Me,
            BurndownReportUserSettings.teamScopeName,
            teamId
        );
        return {
            aggregation: userSettings[BurndownReportUserSettings.aggregationKey],
            startDate: userSettings[BurndownReportUserSettings.startDateKey],
            endDate: userSettings[BurndownReportUserSettings.endDateKey],
            iterationId: userSettings[BurndownReportUserSettings.iterationIdKey],
            workItemTypeFilter: userSettings[BurndownReportUserSettings.workItemTypeFilterKey],
            showNonWorkingDays: userSettings[BurndownReportUserSettings.showNonWorkingDaysKey],
            showRemainingCapacityAtStartOfDay: userSettings[BurndownReportUserSettings.showRemainingCapacityAtStartOfDayKey],
            showResolvedItemsAsCompleted: userSettings[BurndownReportUserSettings.showResolvedItemsAsCompletedKey]
        } as BurndownReportUserSettingsData;
    }
}

export function getBurndownReportSettingsStorageKey(): string {
    return "BurndownReport";
}
